import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, IconButton, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PersonRemoveIcon from "@material-ui/icons/HighlightOff";
import Pagination from "@material-ui/lab/Pagination";
import { ThreeDots } from "react-loader-spinner";
import { useSnackbar } from "notistack";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";
import CommonButton from "../button";
import MemberBadges from "./MemberBadges";
import { PAGE_SIZE, toMessages, memberFullName } from "./utils";
import {
  getGroupMembersAction,
  getGroupMembersErrorAction,
  addGroupMembersAction,
  addGroupMembersSuccessAction,
  addGroupMembersErrorAction,
  removeGroupMembersAction,
  removeGroupMembersSuccessAction,
  removeGroupMembersErrorAction,
  getEmployeeListAction,
} from "../../containers/dashboard/action";

const SEARCH_PAGE_SIZE = 8;

// Optional deployment-scoped company filter. When REACT_APP_COMPANY_ID is set,
// the member picker only searches employees of that company; when it's absent,
// no company_id is sent so the API returns employees across all companies.
// Mirrors how other callers filter getEmployeeList (they pass `company_id`).
const ENV_COMPANY_ID = process.env.REACT_APP_COMPANY_ID;
const companyFilter = ENV_COMPANY_ID ? { company_id: ENV_COMPANY_ID } : {};

const useStyles = makeStyles(() => ({
  paper: {
    width: "560px",
    maxWidth: "100vw",
    padding: "28px 32px 40px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "4px",
  },
  title: {
    color: theme.colors.Violet,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: "22px",
  },
  subtitle: {
    color: theme.colors.DodgerBlue,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: "13px",
    marginBottom: "20px",
  },
  addBox: {
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "10px",
    padding: "14px",
    marginBottom: "22px",
  },
  addLabel: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
    marginBottom: "8px",
  },
  searchWrap: { position: "relative" },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPRO,
    color: theme.colors.Violet,
    backgroundColor: theme.colors.Zumthor,
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "6px",
    outline: "none",
  },
  results: {
    marginTop: "8px",
    border: `1px solid ${theme.colors.Zumthor}`,
    borderRadius: "8px",
    maxHeight: "220px",
    overflowY: "auto",
  },
  resultRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
    cursor: "pointer",
    gap: "10px",
  },
  resultName: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
  },
  resultSub: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.6,
  },
  addTag: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.DodgerBlue,
    whiteSpace: "nowrap",
  },
  searchState: {
    padding: "14px",
    textAlign: "center",
    fontSize: "13px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.7,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "12px" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: theme.colors.Zumthor,
    color: theme.colors.Violet,
    borderRadius: "999px",
    padding: "3px 10px",
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    maxWidth: "100%",
  },
  chipX: { cursor: "pointer", fontFamily: theme.fontFamily.MARKPROBOLD },
  addActions: {
    marginTop: "12px",
    display: "flex",
    justifyContent: "flex-end",
    width: "200px",
    marginLeft: "auto",
  },
  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  listTitle: {
    fontSize: "15px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
  },
  list: { flex: 1, overflowY: "auto" },
  memberRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 4px",
    borderBottom: `1px solid ${theme.colors.Zumthor}`,
    gap: "10px",
  },
  memberName: {
    fontSize: "14px",
    fontFamily: theme.fontFamily.MARKPROMEDIUM,
    color: theme.colors.Violet,
  },
  memberEmail: {
    fontSize: "12px",
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: theme.colors.Violet,
    opacity: 0.6,
  },
  empty: {
    padding: "40px 0",
    textAlign: "center",
    color: theme.colors.Violet,
    opacity: 0.7,
    fontFamily: theme.fontFamily.MARKPROBOOK,
  },
  loading: { display: "flex", justifyContent: "center", padding: "40px" },
  pagination: { display: "flex", justifyContent: "center", marginTop: "14px" },
}));

const employeeLabel = (emp) => {
  const name = [emp?.first_name, emp?.last_name].filter(Boolean).join(" ").trim();
  return name || emp?.email || emp?.id || "—";
};
const employeeSub = (emp) =>
  emp?.email || emp?.company?.name || emp?.employee_code || "";

function ManageMembersDrawer({ open, group, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [stagedUsers, setStagedUsers] = useState([]); // [{ id, label }]
  const [removingId, setRemovingId] = useState(null);

  const membersList = useSelector((s) => s.dashboardReducer?.groupMembersList);
  const loading = useSelector((s) => !!s.dashboardReducer?.groupMembersLoading);
  const listError = useSelector((s) => s.dashboardReducer?.groupMembersError);
  const mutationLoading = useSelector((s) => !!s.dashboardReducer?.memberMutationLoading);
  const addSuccess = useSelector((s) => s.dashboardReducer?.addMembersSuccess);
  const addError = useSelector((s) => s.dashboardReducer?.addMembersError);
  const removeSuccess = useSelector((s) => s.dashboardReducer?.removeMembersSuccess);
  const removeError = useSelector((s) => s.dashboardReducer?.removeMembersError);
  // Employee search (shared list slice) powers the member picker.
  const employeeList = useSelector((s) => s.dashboardReducer?.employeeList);
  const searching = useSelector((s) => !!s.dashboardReducer?.loading);

  const members = membersList?.data || [];
  const count = membersList?.count || 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const groupId = group?.id;

  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const stagedIds = useMemo(() => new Set(stagedUsers.map((u) => u.id)), [stagedUsers]);

  // Search results, minus users already staged or already shown as members.
  const searchResults = useMemo(() => {
    const data = Array.isArray(employeeList?.data) ? employeeList.data : [];
    return data.filter(
      (e) =>
        e?.id &&
        !stagedIds.has(e.id) &&
        !memberIds.has(e.id) &&
        !e.is_super &&
        !e.is_admin &&
        e.is_active !== false
    );
  }, [employeeList, stagedIds, memberIds]);

  const fetchMembers = useCallback(
    (p) => {
      if (!groupId) return;
      dispatch(getGroupMembersAction({ page: p, page_size: PAGE_SIZE }, groupId));
    },
    [dispatch, groupId]
  );

  useEffect(() => {
    if (open && groupId) fetchMembers(page);
  }, [open, groupId, page, fetchMembers]);

  // Reset transient UI when opening for a different group.
  useEffect(() => {
    if (open) {
      setPage(1);
      setStagedUsers([]);
      setSearchTerm("");
    }
  }, [open, groupId]);

  // Live employee search on change (debounced). No term -> no request / no results.
  useEffect(() => {
    if (!open) return undefined;
    const term = searchTerm.trim();
    if (!term) return undefined;
    const handle = setTimeout(() => {
      dispatch(
        getEmployeeListAction({
          search: term,
          page: 1,
          page_size: SEARCH_PAGE_SIZE,
          // Scope to the configured company when provided; otherwise search all.
          ...companyFilter,
        })
      );
    }, 400);
    return () => clearTimeout(handle);
  }, [searchTerm, open, dispatch]);

  /* ----------------------- notistack + state resets ---------------------- */
  useEffect(() => {
    if (!listError) return;
    toMessages(listError, "Unable to load members.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(getGroupMembersErrorAction(null));
  }, [listError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!addSuccess) return;
    enqueueSnackbar("Members added.", { variant: "success" });
    dispatch(addGroupMembersSuccessAction(null));
    setStagedUsers([]);
    setSearchTerm("");
  }, [addSuccess, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!addError) return;
    toMessages(addError, "Unable to add members.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(addGroupMembersErrorAction(null));
  }, [addError, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!removeSuccess) return;
    enqueueSnackbar("Member removed.", { variant: "success" });
    dispatch(removeGroupMembersSuccessAction(null));
    setRemovingId(null);
  }, [removeSuccess, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (!removeError) return;
    toMessages(removeError, "Unable to remove member.").forEach((msg) =>
      enqueueSnackbar(msg, { variant: "error" })
    );
    dispatch(removeGroupMembersErrorAction(null));
    setRemovingId(null);
  }, [removeError, enqueueSnackbar, dispatch]);

  /* ------------------------------ handlers ------------------------------- */
  const stageUser = (emp) => {
    if (stagedIds.has(emp.id)) return;
    setStagedUsers((prev) => [...prev, { id: emp.id, label: employeeLabel(emp) }]);
  };
  const unstageUser = (id) => setStagedUsers((prev) => prev.filter((u) => u.id !== id));

  const submitAdd = () => {
    if (!stagedUsers.length || !groupId) return;
    dispatch(addGroupMembersAction({ user_ids: stagedUsers.map((u) => u.id) }, groupId));
  };

  const removeMember = (member) => {
    if (!groupId) return;
    setRemovingId(member.id);
    dispatch(removeGroupMembersAction({ user_ids: [member.id] }, groupId));
  };

  const subtitle = useMemo(() => {
    if (!group) return "";
    return `${group.name} · ${count} member${count === 1 ? "" : "s"}`;
  }, [group, count]);

  const trimmed = searchTerm.trim();

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <div className={classes.title}>Manage members</div>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.subtitle}>{subtitle}</div>

      {/* Add members — search users and stage them */}
      <div className={classes.addBox}>
        <div className={classes.addLabel}>Add members</div>
        <div className={classes.searchWrap}>
          <input
            className={classes.searchInput}
            placeholder="Search by name, email or employee code"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search users to add"
          />

          {trimmed ? (
            <div className={classes.results}>
              {searching ? (
                <div className={classes.searchState}>Searching…</div>
              ) : searchResults.length === 0 ? (
                <div className={classes.searchState}>No users found for “{trimmed}”.</div>
              ) : (
                searchResults.map((emp) => (
                  <div
                    key={emp.id}
                    className={classes.resultRow}
                    onClick={() => stageUser(emp)}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className={classes.resultName}>
                        {employeeLabel(emp)} <MemberBadges member={emp} />
                      </div>
                      {employeeSub(emp) ? (
                        <div className={classes.resultSub}>{employeeSub(emp)}</div>
                      ) : null}
                    </div>
                    <span className={classes.addTag}>+ Add</span>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>

        {stagedUsers.length ? (
          <>
            <div className={classes.chips}>
              {stagedUsers.map((u) => (
                <span key={u.id} className={classes.chip} title={u.label}>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "180px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {u.label}
                  </span>
                  <span className={classes.chipX} onClick={() => unstageUser(u.id)}>
                    ×
                  </span>
                </span>
              ))}
            </div>
            <div className={classes.addActions}>
              <CommonButton
                type="button"
                text={`Add ${stagedUsers.length} member${stagedUsers.length === 1 ? "" : "s"}`}
                stylevariant="primary"
                fontSize="14px"
                loading={mutationLoading}
                disabled={mutationLoading}
                onClick={submitAdd}
              />
            </div>
          </>
        ) : null}
      </div>

      {/* Current members */}
      <div className={classes.listHeader}>
        <span className={classes.listTitle}>Current members</span>
      </div>

      <div className={classes.list}>
        {loading && !members.length ? (
          <div className={classes.loading}>
            <ThreeDots color={theme.colors.DodgerBlue} height={44} width={44} />
          </div>
        ) : members.length === 0 ? (
          <div className={classes.empty}>No members in this group yet.</div>
        ) : (
          members.map((member) => {
            const inactive = member.is_active === false;
            const isRemoving = removingId === member.id && mutationLoading;
            return (
              <div
                key={member.id}
                className={classes.memberRow}
                style={{ opacity: inactive ? 0.55 : 1 }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className={classes.memberName}>
                    {memberFullName(member)} <MemberBadges member={member} />
                  </div>
                  <div className={classes.memberEmail}>{member.email}</div>
                </div>
                <Tooltip title="Remove from group">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Remove ${memberFullName(member)}`}
                      disabled={mutationLoading}
                      onClick={() => removeMember(member)}
                    >
                      {isRemoving ? (
                        <ThreeDots color={theme.colors.RejectRed} height={18} width={18} />
                      ) : (
                        <PersonRemoveIcon fontSize="small" style={{ color: theme.colors.RejectRed }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            );
          })
        )}
      </div>

      {count > PAGE_SIZE ? (
        <div className={classes.pagination}>
          <Pagination
            count={totalPages}
            page={page}
            color="primary"
            shape="rounded"
            size="small"
            onChange={(_, value) => setPage(value)}
          />
        </div>
      ) : null}
    </Drawer>
  );
}

export default ManageMembersDrawer;
